import { AreaOccupancySlotData } from '../../api';
import { AgendaCardState, AreaSeverity, severityAt } from './utils/occupancyMath';
import { DEFAULT_ORDER_PAGE_COLOR, mb } from '../../common/theme/mbTokens';

/**
 * The areas view borrows the calendar picker's availability palette so the timeline strip reads
 * like the rest of the product. Blocked resources are drawn as hatched grey — a different kind of
 * "unavailable" from simply being full.
 */
export const AREAS_PALETTE = {
  free: '#4CAF50',
  tight: '#FFC107',
  full: '#FF5A5F',
  blocked: mb.control,
  blockedHatch: `repeating-linear-gradient(45deg, ${mb.control}, ${mb.control} 3px, ${mb.side} 3px, ${mb.side} 6px)`,
  defaultPageColor: DEFAULT_ORDER_PAGE_COLOR,
  nowMarker: mb.terracotta,
};

/**
 * Shades of green from "barely touched" to "nearly out", so a glance along the strip shows how the
 * day fills up rather than a flat block of green. Amber and red still take over once an area is
 * genuinely tight, so this scale only ever covers the comfortable range.
 */
export const FREE_HEAT_SCALE = ['#C8E6C9', '#A5D6A7', '#66BB6A', '#43A047', '#2E7D32'];

export const severityColor = (severity: AreaSeverity): string => {
  switch (severity) {
    case AreaSeverity.CRIT:
      return AREAS_PALETTE.full;
    case AreaSeverity.WARN:
      return AREAS_PALETTE.tight;
    case AreaSeverity.BLOCKED_ALL:
      return AREAS_PALETTE.blocked;
    default:
      return AREAS_PALETTE.free;
  }
};

export const severityBackground = (severity: AreaSeverity): string =>
  severity === AreaSeverity.BLOCKED_ALL ? AREAS_PALETTE.blockedHatch : severityColor(severity);

/** Fewest free resources that still counts as comfortable; below this the strip turns amber. */
export const LOWEST_GREEN_FREE = 3;

/**
 * Position within the green band, 0 (empty) to 1 (down to the last comfortable resource).
 *
 * The scale is stretched over the band green actually occupies rather than over full occupancy:
 * amber takes over at LOWEST_GREEN_FREE, so spreading the shades across 0-100% would leave the
 * darkest greens unreachable and the whole strip looking uniformly pale.
 */
export const greenHeatProgress = (slotData: AreaOccupancySlotData): number => {
  const usable = slotData.total - slotData.blocked;
  const span = usable - LOWEST_GREEN_FREE;
  if (span <= 0) {
    return 0;
  }
  return Math.min(Math.max((usable - slotData.free) / span, 0), 1);
};

export const EMPTY_SLOT_BACKGROUND = 'transparent';

/** Background for one timeline cell: nothing while no order sits in it, the heat scale while there is
    room, alarm colours after. */
export const slotBackground = (slotData: AreaOccupancySlotData | undefined): string => {
  const severity = severityAt(slotData);
  if (severity !== AreaSeverity.OK || !slotData) {
    return severityBackground(severity);
  }
  if (slotData.occupied === 0) {
    return EMPTY_SLOT_BACKGROUND;
  }
  const step = Math.round(greenHeatProgress(slotData) * (FREE_HEAT_SCALE.length - 1));
  return FREE_HEAT_SCALE[Math.min(Math.max(step, 0), FREE_HEAT_SCALE.length - 1)];
};

/** Quiet token tints: a full afternoon of cards stays calm to look at. */
export const CARD_FILL: Record<AgendaCardState, string> = {
  [AgendaCardState.PENDING]: mb.surface,
  [AgendaCardState.NO_SHOW]: mb.ground,
  [AgendaCardState.NOT_ARRIVED]: mb.surface,
  [AgendaCardState.NEEDS_PROJECT]: mb.claySoft,
  [AgendaCardState.OPEN_BILL]: mb.claySoft,
  [AgendaCardState.HERE]: mb.todayWash,
  [AgendaCardState.DONE]: mb.todayWash,
};
